import { debounce, uniqBy } from 'lodash'
import { unwrapResult } from '@reduxjs/toolkit'
import { Formik, useFormikContext } from 'formik'
import React, { useEffect, useImperativeHandle, useRef } from 'react'
import * as Yup from 'yup'
import {
  Box,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from '@material-ui/core'
import { Cancel as CancelIcon } from '@material-ui/icons'

import {
  Button,
  Form,
  FIELD_REQUIRED,
  FormikTextField,
  FormikRadioGroup,
} from 'src/modules/ui'

import { useActionRunner } from 'src/modules/notification'
import StepForm from './StepForm'
import { useRecipes } from '../../hooks'
import { mapStepDataToFormData, mapIngredientDataToFormData } from '../../utils'

const RecipeForm = React.forwardRef(
  ({ ingredients, equipment, jobTypes, title, recipe }, ref) => {
    const initialValues = { newUnit: 'g' }

    const stepFormRefs = useRef([])

    const [stepData, setStepData] = React.useState({
      steps:
        recipe && recipe.steps ? recipe.steps.map(mapStepDataToFormData) : [],
    })

    const [currentStepPosition, setCurrentStepPosition] = React.useState(0)

    const [searchTerm, setSearchTerm] = React.useState('')
    const [selectedIngredients, setSelectedIngredients] = React.useState(
      recipe && recipe.recipeIngredients
        ? uniqBy(
            recipe.recipeIngredients.map(mapIngredientDataToFormData),
            'ingredientId'
          )
        : []
    )

    const [ingredientQuantity, setIngredientQuantity] = React.useState({})

    const [newUnit, setNewUnit] = React.useState()
    const [newQuantity, setNewQuantity] = React.useState('')

    const {
      searchIngredientsAndRecipes,
      searchIngredientsAndRecipesResults,
      createIngredient,
    } = useRecipes()

    const [doCreateIngredient] = useActionRunner(createIngredient)
    // eslint-disable-next-line no-unused-vars
    const [doSearchIngredientsAndRecipes, isSearching] = useActionRunner(
      searchIngredientsAndRecipes
    )

    useEffect(() => {
      addStep()
    }, [])

    useEffect(() => {
      if (searchTerm.length < 3) return
      const debouncedSearch = debounce(() => {
        doSearchIngredientsAndRecipes(searchTerm)
      }, 500)
      debouncedSearch()
      return () => {
        debouncedSearch.cancel()
      }
    }, [searchTerm])

    const filteredResults = searchIngredientsAndRecipesResults

    const createNewIngredient = async e => {
      const result = await doCreateIngredient({
        name: searchTerm,
        unit: newUnit,
        agreement_products: [],
        external_products: [],
      })
      const ingredient = unwrapResult(result)

      setSearchTerm('')

      setSelectedIngredients(prevIngredients => [
        ...prevIngredients,
        {
          ingredientId: ingredient.id,
          ingredientName: ingredient.name,
          ingredientUnit: ingredient.unit,
          quantity: newQuantity,
          ingredientType: 'ingredient',
        },
      ])
    }

    const addIngredient = ingredientId => {
      const ingredient = searchIngredientsAndRecipesResults.find(
        i => i.ingredientId === ingredientId
      )

      if (ingredientQuantity[ingredientId]) {
        setSelectedIngredients(prevIngredients => [
          ...prevIngredients,
          { ...ingredient, quantity: ingredientQuantity[ingredientId] },
        ])
        delete ingredientQuantity[ingredientId]
      }
    }

    const removeIngredient = ingredientId => {
      setSelectedIngredients(prevIngredients =>
        prevIngredients.filter(
          ingredient => ingredient.ingredientId !== ingredientId
        )
      )
    }

    const updateIngredientQuantity = (quantity, ingredientId) => {
      setIngredientQuantity(prevQuantity => ({
        ...prevQuantity,
        [ingredientId]: quantity,
      }))
    }

    const addStep = () => {
      const position = currentStepPosition + 1

      setStepData(prevFormInfo => ({
        ...prevFormInfo,
        steps: [
          ...prevFormInfo.steps,
          {
            text: '',
            position,
            jobType: { id: null, time: null },
            equipmentType: { id: null, time: null },
            ref: stepFormRefs.current[position],
          },
        ],
      }))
      setCurrentStepPosition(position)
    }

    const FormObserver = () => {
      const { values } = useFormikContext()

      if (values.newUnit) {
        setNewUnit(values.newUnit)
      }

      useImperativeHandle(ref, () => ({
        getState() {
          return { values, stepData, stepFormRefs, selectedIngredients }
        },
      }))

      return null
    }

    //////////////////////////////////////////////
    // Initialise as edit if recipe is provided //
    //////////////////////////////////////////////

    if (recipe) {
      initialValues.name = recipe.name
    }

    const validationSchema = Yup.object().shape({
      name: Yup.string()
        .required(FIELD_REQUIRED)
        .min(3, 'Must be at least three characters'),
      description: Yup.string(),
    })

    const handleStepChange = step => {
      const index =
        stepData.steps.findIndex(s => s.position === step.position) ?? 0
      setStepData(prevFormInfo => {
        const updatedSteps = [...prevFormInfo.steps]
        updatedSteps[index] = step
        return {
          ...prevFormInfo,
          steps: updatedSteps,
        }
      })
    }

    return (
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        enableReinitialize
      >
        {() => (
          <>
            <Form>
              <FormObserver />
              <Form.InputGroup title={title}>
                <FormikTextField
                  label="Recipe Name"
                  margin="normal"
                  name="name"
                  required
                  fullWidth
                />
              </Form.InputGroup>

              <Form.InputGroup title="Ingredients">
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    {selectedIngredients.map(selectedIngredient => (
                      <Grid
                        container
                        direction="row"
                        spacing={2}
                        alignItems="center"
                      >
                        <Grid item xs={2}>
                          <Typography>
                            {+selectedIngredient.quantity}
                            {selectedIngredient.ingredientUnit}
                          </Typography>
                        </Grid>
                        <Grid item xs={8}>
                          <Typography>
                            {selectedIngredient.ingredientName}
                          </Typography>
                        </Grid>
                        <Grid item xs={2} container justifyContent="flex-end">
                          <IconButton
                            onClick={() =>
                              removeIngredient(selectedIngredient.ingredientId)
                            }
                            size="small"
                          >
                            <CancelIcon />
                          </IconButton>
                        </Grid>
                      </Grid>
                    ))}
                  </Grid>
                </Grid>
                <Grid container spacing={2}>
                  <Grid item md={12} xs={12}>
                    <FormikTextField
                      label="Search"
                      margin="normal"
                      name="searchIngredients"
                      onChange={e => setSearchTerm(e.target.value)}
                      fullWidth
                    />
                  </Grid>
                </Grid>
                <Grid container spacing={2}>
                  {filteredResults.length > 0
                    ? filteredResults.map(ingredient => (
                        <Grid item md={6} xs={12} key={ingredient.ingredientId}>
                          <Box
                            bgcolor="background.paper"
                            borderRadius={10}
                            p={2}
                          >
                            <Grid
                              container
                              spacing={2}
                              justifyContent="center"
                              alignItems="center"
                            >
                              <Grid item xs={6}>
                                <Typography style={{ marginTop: '0.4rem' }}>
                                  {ingredient.ingredientName}
                                </Typography>
                              </Grid>
                              <Grid item xs={4}>
                                <TextField
                                  size="small"
                                  variant="filled"
                                  onChange={e =>
                                    updateIngredientQuantity(
                                      e.target.value,
                                      ingredient.ingredientId
                                    )
                                  }
                                  inputProps={{
                                    style: { paddingTop: 8, paddingBottom: 8 },
                                  }}
                                  InputProps={{
                                    endAdornment: ingredient.ingredientUnit && (
                                      <InputAdornment position="end">
                                        {`${ingredient.ingredientUnit}`}
                                      </InputAdornment>
                                    ),
                                  }}
                                ></TextField>
                              </Grid>

                              <Grid item md={2} xs={2}>
                                <Button
                                  onClick={e => {
                                    addIngredient(ingredient.ingredientId)
                                  }}
                                  color="lightGreen"
                                  disabled={
                                    ingredientQuantity[
                                      ingredient.ingredientId
                                    ] === undefined
                                  }
                                >
                                  Add
                                </Button>
                              </Grid>
                            </Grid>
                          </Box>
                        </Grid>
                      ))
                    : null}
                  {searchTerm !== '' && (
                    <Grid item md={6} xs={12} key={'new'}>
                      <Box bgcolor="background.paper" borderRadius={10} p={1.7}>
                        <Grid
                          container
                          spacing={1}
                          justifyContent="center"
                          alignItems="center"
                        >
                          <Grid item xs={6}>
                            <FormikRadioGroup
                              fullWidth
                              key="newUnit"
                              label="Unit"
                              margin="normal"
                              name="newUnit"
                              options={[
                                { value: 'g', label: 'grams' },
                                { value: 'ml', label: 'ml' },
                              ]}
                              table={true}
                            />
                          </Grid>
                          <Grid item xs={3}>
                            <TextField
                              variant="filled"
                              onChange={e => setNewQuantity(e.target.value)}
                              inputProps={{
                                style: { paddingTop: 8, paddingBottom: 8 },
                              }}
                              InputProps={{
                                endAdornment: newUnit && (
                                  <InputAdornment position="end">
                                    {`${newUnit}`}
                                  </InputAdornment>
                                ),
                              }}
                            ></TextField>
                          </Grid>
                          <Grid item xs={3}>
                            <Button
                              onClick={e => {
                                createNewIngredient(e)
                              }}
                              color="lightGreen"
                              style={{ marginTop: '0.2rem' }}
                              disabled={newQuantity === ''}
                            >
                              Create
                            </Button>
                          </Grid>
                        </Grid>
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </Form.InputGroup>

              <Form.InputGroup title="Steps">
                {stepData.steps
                  ? stepData.steps.map((step, index) => (
                      <>
                        <StepForm
                          jobTypes={jobTypes}
                          equipment={equipment}
                          step={step}
                          index={index}
                          handler={handleStepChange}
                          key={index}
                          ref={ref => (stepFormRefs.current[index] = ref)}
                        />
                      </>
                    ))
                  : null}
              </Form.InputGroup>

              <Button
                color="primary"
                onClick={addStep}
                size="large"
                style={{ marginTop: '0.6rem' }}
              >
                Add Step
              </Button>
            </Form>
          </>
        )}
      </Formik>
    )
  }
)

export default RecipeForm
